import { StoreModel } from './index'
import incense from '@mortgage-pos/ui/incense'
import http from '@mortgage-pos/ui/services/http'
import { isHttpError } from '@mortgage-pos/shared-http'
import {
  FigureOffer,
  FigureSubmissionError,
  FIGURE_SUBMISSION_REJECTED,
  FIGURE_ADDRESS_CORRECTION_REQUIRED,
  FigureOffersResponse,
  categorizeFigureReasons,
} from '@mortgage-pos/types'
import { environment } from '@mortgage-pos/ui/env'
import { Action, action, thunk, Thunk } from 'easy-peasy'

const FIGURE_OFFERS_TOTAL_BUDGET_MS = 180 * 1000
const FIGURE_OFFERS_REPOLL_DELAY_MS = 1000

// Shared so concurrent triggers (loader double-mount) reuse one polling loop,
// keyed by guid so a pending poll is never reused for another application.
let figureOffersPoll: {
  guid: string
  promise: Promise<FigureOffersResponse | FigureSubmissionError | null>
} | null = null

// Shared so a StrictMode double-mount awaits the same in-flight post instead
// of skipping past it, keeping leadSubmitted ordered before later statuses.
let leadSubmittedPost: Promise<void> | null = null

type ApplicationFigureError = FigureSubmissionError & {
  applicationGuid: string
}

export interface HomeEquityModel {
  figureSubmissionError: ApplicationFigureError | null
  figureAddressCorrectionGuid: string | null
  setFigureSubmissionError: Action<
    HomeEquityModel,
    ApplicationFigureError | null
  >
  setFigureAddressCorrectionGuid: Action<HomeEquityModel, string | null>
  figureOffersResponse: FigureOffersResponse | null
  selectedOfferId: string
  figureUnlicensedStates: string[]
  hasPostedOffersStatus: boolean
  hasPostedLeadSubmittedStatus: boolean
  setFigureOffersResponse: Action<HomeEquityModel, FigureOffersResponse | null>
  setSelectedOfferId: Action<HomeEquityModel, string>
  setFigureUnlicensedStates: Action<HomeEquityModel, string[]>
  setHasPostedOffersStatus: Action<HomeEquityModel, boolean>
  setHasPostedLeadSubmittedStatus: Action<HomeEquityModel, boolean>
  postOffersStatus: Thunk<HomeEquityModel, string, null, StoreModel>
  postLeadSubmittedStatus: Thunk<HomeEquityModel, string, null, StoreModel>
  getFigureOffers: Thunk<HomeEquityModel, void, object, StoreModel>
  getFigureRedirectUrl: Thunk<HomeEquityModel, void, object, StoreModel>
  getFigureContinueUrl: Thunk<
    HomeEquityModel,
    void,
    object,
    StoreModel,
    Promise<string | null>
  >
  getFigureUnlicensedStates: Thunk<HomeEquityModel, void, object, StoreModel>
  repullCredit: Thunk<HomeEquityModel, void, object, StoreModel>
  getOffersAfterCreditUnfreeze: Thunk<
    HomeEquityModel,
    void,
    object,
    StoreModel,
    Promise<boolean>
  >
}

const homeEquity = (): HomeEquityModel => {
  return {
    figureSubmissionError: null,
    figureAddressCorrectionGuid: null,
    setFigureSubmissionError: action((state, payload) => {
      state.figureSubmissionError = payload
    }),
    setFigureAddressCorrectionGuid: action((state, payload) => {
      state.figureAddressCorrectionGuid = payload
    }),
    figureOffersResponse: null,
    selectedOfferId: '',
    figureUnlicensedStates: [],
    hasPostedOffersStatus: false,
    hasPostedLeadSubmittedStatus: false,
    setSelectedOfferId: action((state, payload) => {
      state.selectedOfferId = payload
    }),
    setFigureOffersResponse: action((state, payload) => {
      state.figureOffersResponse = payload
    }),
    setFigureUnlicensedStates: action((state, payload) => {
      state.figureUnlicensedStates = payload
    }),
    setHasPostedOffersStatus: action((state, payload) => {
      state.hasPostedOffersStatus = payload
    }),
    setHasPostedLeadSubmittedStatus: action((state, payload) => {
      state.hasPostedLeadSubmittedStatus = payload
    }),
    // Store-level dedupe so the offers/no-offers status posts once per
    // session; released on failure so a later trigger can retry.
    postOffersStatus: thunk(
      async (actions, status, { getState, getStoreActions }) => {
        if (getState().hasPostedOffersStatus) return
        actions.setHasPostedOffersStatus(true)

        const posted = await getStoreActions().application.updateStatus(status)
        if (!posted) {
          actions.setHasPostedOffersStatus(false)
        }
      }
    ),
    // Same session-level dedupe as postOffersStatus, single-flight so every
    // concurrent caller returns only after the status has actually posted.
    postLeadSubmittedStatus: thunk(
      async (actions, status, { getState, getStoreActions }) => {
        if (!leadSubmittedPost && !getState().hasPostedLeadSubmittedStatus) {
          leadSubmittedPost = (async () => {
            actions.setHasPostedLeadSubmittedStatus(true)

            const posted =
              await getStoreActions().application.updateStatus(status)
            if (!posted) {
              actions.setHasPostedLeadSubmittedStatus(false)
            }
          })().finally(() => {
            leadSubmittedPost = null
          })
        }

        await leadSubmittedPost
      }
    ),
    getFigureOffers: thunk(async (actions, _, { getStoreState }) => {
      // DEV ONLY (local): force a no-offer category to preview each no-offers
      // screen without a live Figure response. In the browser console run e.g.
      //   localStorage.setItem('figureDebugReasons', 'CREDITSCORE')
      // then reload the offers step; clear it to restore normal behavior.
      if (environment.appEnv === 'local') {
        const debugReasons = window.localStorage.getItem('figureDebugReasons')
        if (debugReasons) {
          actions.setFigureOffersResponse({
            nextInquiryStep: 'SELECT_PROPERTY',
            status: 'noRates',
            reasonCategory: categorizeFigureReasons(debugReasons.split(',')),
          })
          return true
        }
      }

      const { applicationGuid } = getStoreState().application

      if (!applicationGuid) {
        return false
      }

      if (
        getStoreState().homeEquity.figureSubmissionError?.applicationGuid ===
        applicationGuid
      ) {
        return false
      }

      if (figureOffersPoll?.guid !== applicationGuid) {
        const promise = pollForFigureOffers(applicationGuid).finally(() => {
          if (figureOffersPoll?.promise === promise) {
            figureOffersPoll = null
          }
        })
        figureOffersPoll = { guid: applicationGuid, promise }
      }

      const response = await figureOffersPoll.promise

      // The application changed while polling; drop this stale result.
      if (getStoreState().application.applicationGuid !== applicationGuid) {
        return false
      }

      if (!response) {
        incense(new Error('No Figure offers response within polling budget'))
          .details({
            name: 'getFigureOffers',
            message: 'Error getting offers',
          })
          .safe({
            applicationGuid,
          })
          .error()
        // TODO(figure-failure): unknown outcome — show the no-offers screen;
        // status stays homeEquityFlowTouched. Revisit status + 180s budget.
        actions.setFigureOffersResponse(null)
        return true
      }

      if ('code' in response) {
        actions.setFigureOffersResponse(null)
        actions.setFigureSubmissionError({ ...response, applicationGuid })
        actions.setFigureAddressCorrectionGuid(
          response.code === FIGURE_ADDRESS_CORRECTION_REQUIRED
            ? applicationGuid
            : null
        )
        return false
      }

      actions.setFigureSubmissionError(null)
      actions.setFigureAddressCorrectionGuid(null)
      if (response.offers) {
        response.offers = sortAndPickBestOffers(response.offers)
      }
      actions.setFigureOffersResponse(response)

      return true
    }),
    getFigureRedirectUrl: thunk(async (actions, _, { getStoreState }) => {
      const { applicationGuid } = getStoreState().application
      const { selectedOfferId, figureOffersResponse } =
        getStoreState().homeEquity

      if (!applicationGuid || !selectedOfferId) {
        return null
      }

      const offerMaxAmount = figureOffersResponse?.offers?.find(
        (offer) => offer.uuid === selectedOfferId
      )?.maxAmount

      const offerMinAmount = figureOffersResponse?.offers?.find(
        (offer) => offer.uuid === selectedOfferId
      )?.minAmount

      try {
        const redirectUrl = await fetchFigureRedirectUrl(
          applicationGuid,
          selectedOfferId,
          offerMaxAmount,
          offerMinAmount
        )
        return redirectUrl
      } catch (error) {
        incense(error)
          .details({
            name: 'getFigureRedirectUrl',
            message: 'Error getting Figure redirect URL',
          })
          .safe({
            applicationGuid,
            selectedOfferId,
          })
          .error()
        return null
      }
    }),
    // Fetches the borrower's Figure portal link for the no-offers "Continue my
    // application" CTA. Unlike getFigureRedirectUrl this needs no selected offer,
    // since there are no offers to select in this flow.
    getFigureContinueUrl: thunk(async (_, __, { getStoreState }) => {
      const { applicationGuid } = getStoreState().application

      if (!applicationGuid) {
        return null
      }

      try {
        const { data } = await http.get(
          `${environment.apiBaseUrl}/api/home-equity/figure-borrower-app-link?applicationGuid=${applicationGuid}`
        )
        return data.borrowerAppLink ?? null
      } catch (error) {
        incense(error)
          .details({
            name: 'getFigureContinueUrl',
            message: 'Error getting Figure borrower app link',
          })
          .safe({
            applicationGuid,
          })
          .error()
        return null
      }
    }),
    getFigureUnlicensedStates: thunk(async (actions) => {
      try {
        const { data } = await http.get(
          `${environment.apiBaseUrl}/api/home-equity/figure-unlicensed-states`
        )
        actions.setFigureUnlicensedStates(data.states)
        return true
      } catch (error) {
        incense(error)
          .details({
            name: 'getFigureUnlicensedStates',
            message: 'Error getting Figure unlicensed states',
          })
          .error()
        return false
      }
    }),
    repullCredit: thunk(async (_, __, { getStoreState }) => {
      const { applicationGuid } = getStoreState().application

      if (!applicationGuid) {
        return false
      }

      try {
        await http.post(
          `${environment.apiBaseUrl}/api/home-equity/repull-credit?applicationGuid=${applicationGuid}`
        )
        return true
      } catch (error) {
        incense(error)
          .details({
            name: 'repullCredit',
            message: 'Error repulling credit from Figure',
          })
          .safe({
            applicationGuid,
          })
          .error()
        return false
      }
    }),
    getOffersAfterCreditUnfreeze: thunk(
      async (actions, _, { getStoreState }) => {
        const { applicationGuid } = getStoreState().application

        if (!applicationGuid) {
          return false
        }

        try {
          const response = await fetchPollAfterCreditUnfreeze(applicationGuid)
          if (response?.offers) {
            response.offers = sortAndPickBestOffers(response.offers)
          }
          actions.setFigureOffersResponse(response)

          const nextStep = response?.nextInquiryStep
          return nextStep !== 'UNFREEZE_CREDIT' && nextStep !== 'UNKNOWN'
        } catch (error) {
          incense(error)
            .details({
              name: 'getOffersAfterCreditUnfreeze',
              message: 'Error polling for offers after credit unfreeze',
            })
            .safe({
              applicationGuid,
            })
            .error()
          return false
        }
      }
    ),
  }
}

export default homeEquity

async function fetchFigureOffers(
  applicationGuid: string
): Promise<FigureOffersResponse | FigureSubmissionError | null> {
  if (!applicationGuid) {
    return null
  }

  try {
    const { data } = await http.get(
      `${environment.apiBaseUrl}/api/home-equity/figure-offers?applicationGuid=${applicationGuid}`
    )
    return data
  } catch (error) {
    if (isHttpError(error)) {
      const status =
        error.response?.status ?? error.response?.statusCode ?? error.statusCode
      const data = error.response?.data
      if (
        status === 422 &&
        [
          FIGURE_ADDRESS_CORRECTION_REQUIRED,
          FIGURE_SUBMISSION_REJECTED,
        ].includes(data?.code) &&
        data?.retryable === false
      ) {
        return {
          code: data.code,
          message:
            data?.code === FIGURE_ADDRESS_CORRECTION_REQUIRED
              ? data.message
              : 'We could not submit your application. Please contact your loan officer for help.',
          retryable: false,
        }
      }
    }
    return null
  }
}

/**
 * Re-polls the offers endpoint until Figure decides. Requests are short-lived
 * server-side, so this loop owns the total wait instead of one long connection.
 */
async function pollForFigureOffers(
  applicationGuid: string
): Promise<FigureOffersResponse | FigureSubmissionError | null> {
  const startTime = Date.now()

  while (Date.now() - startTime < FIGURE_OFFERS_TOTAL_BUDGET_MS) {
    const response = await fetchFigureOffers(applicationGuid)

    if (
      response &&
      ('code' in response || response.nextInquiryStep !== 'UNKNOWN')
    ) {
      return response
    }

    await new Promise((resolve) =>
      setTimeout(resolve, FIGURE_OFFERS_REPOLL_DELAY_MS)
    )
  }

  return null
}

async function fetchFigureRedirectUrl(
  applicationGuid: string,
  offerUuid: string,
  offerMaxAmount: number,
  offerMinAmount: number
) {
  if (!applicationGuid || !offerUuid || !offerMaxAmount || !offerMinAmount) {
    return null
  }

  try {
    const { data } = await http.get(
      `${environment.apiBaseUrl}/api/home-equity/select-figure-offer?applicationGuid=${applicationGuid}&offerUuid=${offerUuid}&offerMaxAmount=${offerMaxAmount}&offerMinAmount=${offerMinAmount}`
    )
    return data.redirectUrl
  } catch {
    return null
  }
}

async function fetchPollAfterCreditUnfreeze(
  applicationGuid: string
): Promise<FigureOffersResponse | null> {
  if (!applicationGuid) {
    return null
  }

  const { data } = await http.get(
    `${environment.apiBaseUrl}/api/home-equity/get-offers-after-credit-unfreeze?applicationGuid=${applicationGuid}`
  )
  return data
}

function sortAndPickBestOffers(offers: FigureOffer[]) {
  return offers.sort((a, b) => a.rate - b.rate)
}
